/**
 * Customize character, reached from the bar on the side.
 *
 * Kate described this precisely, and corrected the obvious assumption while doing it:
 *
 *   "Can you create a name in this game? ... Yes, you can. Okay, so like name tags —
 *    there's not name tags, it's just a little bar on the side. You can push it and
 *    customize character. It's a customized character. You push it and you type down
 *    whatever name you want."
 *
 * So: a tab on the edge of the screen, not a menu item; it opens "Customize character";
 * you type a name. And explicitly NO floating name tags over players' heads — she ruled
 * those out in the same breath.
 */
const STORAGE_KEY = 'animal-attack:character'
const MAX_NAME = 20

export interface Character {
  name: string
}

function load(): Character {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return { name: String(JSON.parse(raw).name ?? '').slice(0, MAX_NAME) }
  } catch {
    // Fall through to the default.
  }
  return { name: '' }
}

export class CustomizeScreen {
  private readonly tab: HTMLButtonElement
  private readonly root: HTMLDivElement
  private readonly input: HTMLInputElement
  private character: Character = load()
  /** Fires when the name changes, so the room can be told. */
  onRename: ((name: string) => void) | null = null
  onOpenChange: ((open: boolean) => void) | null = null

  constructor() {
    // "It's just a little bar on the side. You can push it."
    this.tab = document.createElement('button')
    this.tab.className = 'side-tab'
    this.tab.innerHTML = '<span>CUSTOMIZE<br>CHARACTER</span>'
    this.tab.addEventListener('click', () => this.toggle())
    document.body.append(this.tab)

    this.root = document.createElement('div')
    this.root.className = 'customize'
    this.root.hidden = true

    const panel = document.createElement('div')
    panel.className = 'customize-panel'

    const title = document.createElement('h2')
    title.textContent = 'Customize character'
    panel.append(title)

    const label = document.createElement('label')
    label.textContent = 'Your name'
    label.htmlFor = 'character-name'
    panel.append(label)

    this.input = document.createElement('input')
    this.input.id = 'character-name'
    this.input.type = 'text'
    this.input.maxLength = MAX_NAME
    this.input.placeholder = 'type whatever name you want'
    this.input.value = this.character.name
    this.input.autocomplete = 'off'
    // Keep gameplay keys out of the text box.
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation()
      if (e.key === 'Enter') this.commit()
    })
    panel.append(this.input)

    const hint = document.createElement('p')
    hint.className = 'customize-hint'
    hint.textContent = 'Other players see this in chat. Nobody has a name tag floating over them.'
    panel.append(hint)

    const done = document.createElement('button')
    done.className = 'customize-done'
    done.textContent = 'Done'
    done.addEventListener('click', () => this.commit())
    panel.append(done)

    this.root.append(panel)
    document.body.append(this.root)
  }

  get name(): string {
    return this.character.name || 'You'
  }

  get isOpen(): boolean {
    return !this.root.hidden
  }

  open(): void {
    this.root.hidden = false
    this.tab.classList.add('is-open')
    this.input.value = this.character.name
    this.input.focus()
    this.onOpenChange?.(true)
  }

  close(): void {
    this.root.hidden = true
    this.tab.classList.remove('is-open')
    this.onOpenChange?.(false)
  }

  toggle(): void {
    if (this.isOpen) this.close()
    else this.open()
  }

  private commit(): void {
    const name = this.input.value.trim().slice(0, MAX_NAME)
    this.character = { name }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.character))
    } catch {
      // Private browsing can refuse writes; the name still applies this session.
    }
    if (name) this.onRename?.(name)
    this.close()
  }
}
